import { useEffect, useState } from "react";
import { NavLink, Link, Outlet, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import logoHeader from "@/assets/logo-header.png";
import logoFooter from "@/assets/logo-footer.png";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/services", label: "Services" },
  { to: "/looking-for", label: "What You're Looking For" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact Us" },
];

export default function Layout() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-white text-slate-900">
      {/* Header */}
      <header
        data-testid="site-header"
        className="sticky top-0 z-50 backdrop-blur-xl bg-slate-50/90 border-b border-slate-200"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="h-16 lg:h-20 flex items-center justify-between">
            <Link
              to="/"
              data-testid="nav-logo-link"
              aria-label="T3 Solutions — Home"
              className="flex items-center group"
            >
              <img
                src={logoHeader}
                alt="T3 Solutions"
                className="h-14 lg:h-16 w-auto object-contain"
              />
            </Link>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-8">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`nav-link-${item.to === "/" ? "home" : item.to.replace("/", "")}`}
                  className={({ isActive }) =>
                    `mm-nav-link text-sm font-medium ${
                      isActive ? "text-slate-900 active" : "text-slate-700 hover:text-orange-500"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>

            <div className="hidden lg:block">
              <Button
                asChild
                data-testid="header-cta-looking-for"
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-md h-10 px-5"
              >
                <Link to="/looking-for">Hire Talent</Link>
              </Button>
            </div>

            {/* Mobile toggle */}
            <button
              type="button"
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-slate-300 text-slate-800 hover:bg-white/50 transition-colors"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>

          {/* Mobile menu */}
          {open && (
            <div
              data-testid="mobile-nav"
              className="lg:hidden pb-6 border-t border-slate-200 -mx-6 px-6"
            >
              <nav className="flex flex-col py-4 gap-1">
                {NAV.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/"}
                    data-testid={`mobile-nav-link-${item.to === "/" ? "home" : item.to.replace("/", "")}`}
                    className={({ isActive }) =>
                      `py-3 px-3 rounded-md text-sm font-medium ${
                        isActive
                          ? "bg-white text-slate-900"
                          : "text-slate-700 hover:bg-white hover:text-orange-500"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
                <Button
                  asChild
                  data-testid="mobile-header-cta-looking-for"
                  className="mt-3 bg-blue-600 hover:bg-blue-700 text-white rounded-md h-11"
                >
                  <Link to="/looking-for">Hire Talent</Link>
                </Button>
              </nav>
            </div>
          )}
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer
        data-testid="site-footer"
        className="border-t border-slate-200 bg-slate-50 mt-24"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div>
              <div className="mb-4">
                <img
                  src={logoFooter}
                  alt="T3 Solutions — Tech Tactics Talent"
                  className="h-24 w-auto object-contain"
                />
              </div>
              <p className="text-sm text-slate-600 leading-relaxed max-w-xs">
                Elite talent for product engineering, digital transformation, AI/ML, and beyond — on contract, remote, or full-time.
              </p>
            </div>

            <div>
              <h4 className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium mb-4">
                Navigate
              </h4>
              <ul className="space-y-2 text-sm">
                {NAV.map((item) => (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className="text-slate-700 hover:text-slate-900 transition-colors"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium mb-4">
                Get in touch
              </h4>
              <a
                href="mailto:contact@t3partner.in"
                data-testid="footer-email-link"
                className="text-sm text-slate-900 font-medium hover:text-orange-600 transition-colors"
              >
                contact@t3partner.in
              </a>
              <p className="text-xs text-slate-500 mt-4 leading-relaxed">
                T3 Solutions Pvt Ltd
              </p>
            </div>
          </div>

          <div className="mt-12 pt-6 border-t border-slate-200 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
            <p className="text-xs text-slate-500">
              © {new Date().getFullYear()} T3 Solutions Pvt Ltd. All rights reserved.
            </p>
            <p className="text-xs text-slate-500">
              Crafted with precision.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
